import { create } from 'zustand'
import { supabase, Database } from '../lib/supabase'
import { startGeneration, subscribeToGeneration, generateFallbackWebsite } from '../lib/ai'

type Project = Database['public']['Tables']['projects']['Row']
type Generation = Database['public']['Tables']['generations']['Row']

interface ProjectState {
  projects: Project[]
  currentProject: Project | null
  generations: Generation[]
  loading: boolean
  generating: boolean
  generationStatus: string | null
  activeGenerationId: string | null
  fetchProjects: (userId: string) => Promise<void>
  createProject: (userId: string, name: string, description?: string) => Promise<Project | null>
  updateProject: (projectId: string, updates: Partial<Project>) => Promise<void>
  deleteProject: (projectId: string) => Promise<void>
  setCurrentProject: (project: Project | null) => void
  generateWebsite: (projectId: string, userId: string, prompt: string) => Promise<void>
  fetchGenerations: (projectId: string) => Promise<void>
  checkPendingGenerations: (projectId: string) => Promise<void>
}

export const useProjectStore = create<ProjectState>((set, get) => ({
  projects: [],
  currentProject: null,
  generations: [],
  loading: false,
  generating: false,
  generationStatus: null,
  activeGenerationId: null,

  fetchProjects: async (userId: string) => {
    set({ loading: true })
    const { data, error } = await supabase
      .from('projects')
      .select('*')
      .eq('user_id', userId)
      .order('updated_at', { ascending: false })

    if (!error && data) {
      set({ projects: data })
    }
    set({ loading: false })
  },

  createProject: async (userId: string, name: string, description?: string) => {
    const { data, error } = await supabase
      .from('projects')
      .insert({
        user_id: userId,
        name,
        description,
        html_content: getDefaultHtml(name),
        css_content: getDefaultCss(),
      })
      .select()
      .single()

    if (!error && data) {
      set({ projects: [data, ...get().projects] })
      return data
    }
    return null
  },

  updateProject: async (projectId: string, updates: Partial<Project>) => {
    const { error } = await supabase
      .from('projects')
      .update({ ...updates, updated_at: new Date().toISOString() })
      .eq('id', projectId)

    if (!error) {
      set({
        projects: get().projects.map((p) =>
          p.id === projectId ? { ...p, ...updates } : p
        ),
        currentProject:
          get().currentProject?.id === projectId
            ? { ...get().currentProject!, ...updates }
            : get().currentProject,
      })
    }
  },

  deleteProject: async (projectId: string) => {
    const { error } = await supabase
      .from('projects')
      .delete()
      .eq('id', projectId)

    if (!error) {
      set({
        projects: get().projects.filter((p) => p.id !== projectId),
        currentProject:
          get().currentProject?.id === projectId ? null : get().currentProject,
      })
    }
  },

  setCurrentProject: (project: Project | null) => {
    set({ currentProject: project })
  },

  generateWebsite: async (projectId: string, userId: string, prompt: string) => {
    set({ generating: true, generationStatus: 'Starting generation...' })

    try {
      // Start the async generation job
      const job = await startGeneration(prompt, projectId, userId)
      
      set({ generationStatus: 'AI is thinking... This may take a minute.' })

      // Subscribe to realtime updates
      const unsubscribe = subscribeToGeneration(job.generation_id, (generation) => {
        console.log('Generation update:', generation.status)
        
        if (generation.status === 'completed') {
          // Update project with generated content
          get().updateProject(projectId, {
            html_content: generation.html_output || '',
            css_content: generation.css_output || '',
          })
          set({ generating: false, generationStatus: null })
          unsubscribe()
        } else if (generation.status === 'failed') {
          console.error('Generation failed:', generation.error_message)
          // Fall back to templates
          const fallback = generateFallbackWebsite(prompt)
          get().updateProject(projectId, {
            html_content: fallback.html,
            css_content: fallback.css,
          })
          set({ generating: false, generationStatus: null })
          unsubscribe()
        }
      })

      // Also poll periodically in case realtime misses an update
      const pollInterval = setInterval(async () => {
        const { data } = await supabase
          .from('generations')
          .select('*')
          .eq('id', job.generation_id)
          .single()

        if (data?.status === 'completed' || data?.status === 'failed') {
          clearInterval(pollInterval)
          
          if (data.status === 'completed' && get().generating) {
            get().updateProject(projectId, {
              html_content: data.html_output || '',
              css_content: data.css_output || '',
            })
            set({ generating: false, generationStatus: null })
          } else if (data.status === 'failed' && get().generating) {
            const fallback = generateFallbackWebsite(prompt)
            get().updateProject(projectId, {
              html_content: fallback.html,
              css_content: fallback.css,
            })
            set({ generating: false, generationStatus: null })
          }
          unsubscribe()
        }
      }, 5000) // Poll every 5 seconds

      // Timeout after 5 minutes
      setTimeout(() => {
        if (get().generating) {
          clearInterval(pollInterval)
          unsubscribe()
          console.warn('Generation timed out, using fallback')
          const fallback = generateFallbackWebsite(prompt)
          get().updateProject(projectId, {
            html_content: fallback.html,
            css_content: fallback.css,
          })
          set({ generating: false, generationStatus: null })
        }
      }, 300000) // 5 minute timeout

    } catch (err) {
      console.warn('Generation failed, using fallback templates:', err)
      const fallback = generateFallbackWebsite(prompt)
      await get().updateProject(projectId, {
        html_content: fallback.html,
        css_content: fallback.css,
      })
      set({ generating: false, generationStatus: null })
    }
  },

  fetchGenerations: async (projectId: string) => {
    const { data } = await supabase
      .from('generations')
      .select('*')
      .eq('project_id', projectId)
      .order('created_at', { ascending: false })

    if (data) {
      set({ generations: data })
    }
  },

  // Check if there are any pending generations when user returns to page
  checkPendingGenerations: async (projectId: string) => {
    const { data: pendingGenerations } = await supabase
      .from('generations')
      .select('*')
      .eq('project_id', projectId)
      .eq('status', 'processing')
      .order('created_at', { ascending: false })
      .limit(1)

    if (pendingGenerations && pendingGenerations.length > 0) {
      const generation = pendingGenerations[0]
      console.log('Found pending generation:', generation.id)
      
      set({ 
        generating: true, 
        generationStatus: 'Resuming generation... AI is still working.',
        activeGenerationId: generation.id
      })

      // Subscribe to updates
      const unsubscribe = subscribeToGeneration(generation.id, (updated) => {
        console.log('Pending generation update:', updated.status)
        
        if (updated.status === 'completed') {
          get().updateProject(projectId, {
            html_content: updated.html_output || '',
            css_content: updated.css_output || '',
          })
          set({ generating: false, generationStatus: null, activeGenerationId: null })
          unsubscribe()
        } else if (updated.status === 'failed') {
          console.error('Generation failed:', updated.error_message)
          const fallback = generateFallbackWebsite(generation.prompt)
          get().updateProject(projectId, {
            html_content: fallback.html,
            css_content: fallback.css,
          })
          set({ generating: false, generationStatus: null, activeGenerationId: null })
          unsubscribe()
        }
      })

      // Also poll
      const pollInterval = setInterval(async () => {
        const { data } = await supabase
          .from('generations')
          .select('*')
          .eq('id', generation.id)
          .single()

        if (data?.status === 'completed' || data?.status === 'failed') {
          clearInterval(pollInterval)
          
          if (data.status === 'completed' && get().generating) {
            get().updateProject(projectId, {
              html_content: data.html_output || '',
              css_content: data.css_output || '',
            })
          } else if (data.status === 'failed' && get().generating) {
            const fallback = generateFallbackWebsite(generation.prompt)
            get().updateProject(projectId, {
              html_content: fallback.html,
              css_content: fallback.css,
            })
          }
          set({ generating: false, generationStatus: null, activeGenerationId: null })
          unsubscribe()
        }
      }, 5000)

      // Timeout
      setTimeout(() => {
        if (get().activeGenerationId === generation.id && get().generating) {
          clearInterval(pollInterval)
          unsubscribe()
          set({ generating: false, generationStatus: null, activeGenerationId: null })
        }
      }, 300000)
    }
  },
}))

// Default templates for new projects
function getDefaultHtml(projectName: string): string {
  return `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${projectName}</title>
</head>
<body>
  <header>
    <nav>
      <h1>${projectName}</h1>
    </nav>
  </header>
  <main>
    <section class="hero">
      <h2>Welcome to ${projectName}</h2>
      <p>Start building your beautiful website with AI.</p>
      <button>Get Started</button>
    </section>
  </main>
</body>
</html>`
}

function getDefaultCss(): string {
  return `* {
  margin: 0;
  padding: 0;
  box-sizing: border-box;
}

body {
  font-family: system-ui, sans-serif;
  line-height: 1.6;
  color: #333;
}

header {
  background: #1a1a1f;
  padding: 1rem 2rem;
}

nav h1 {
  color: #fbbf24;
  font-size: 1.5rem;
}

.hero {
  min-height: 80vh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 2rem;
  background: linear-gradient(135deg, #0d0d10 0%, #1a1a1f 100%);
  color: white;
}

.hero h2 {
  font-size: 3rem;
  margin-bottom: 1rem;
}

.hero p {
  font-size: 1.25rem;
  color: #91919f;
  margin-bottom: 2rem;
}

.hero button {
  background: #fbbf24;
  color: #0d0d10;
  border: none;
  padding: 1rem 2rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  transition: transform 0.2s, box-shadow 0.2s;
}

.hero button:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 40px rgba(251, 191, 36, 0.3);
}`
}
